/**
 * Sign in.
 *
 * The server answers identically for an unknown address, a wrong password and
 * a disabled account, and this page repeats that message verbatim rather than
 * guessing at a friendlier one — a more specific message here would undo the
 * server's care about not revealing who has an account.
 */

import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { ForgotPassword, failure } from "@/auth/PasswordReset";
import { BrandMark } from "@/components/ui/BrandMark";
import { Button, FieldShell, TextInput } from "@/components/ui/controls";
import { Notice } from "@/components/ui/states";
import { api } from "@/lib/api";

export function LoginPage() {
  const { signIn } = useAuth();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [forgotten, setForgotten] = useState(false);
  // Hidden until the server says yes: a link that mails nobody is worse than none.
  const options = useQuery({ queryKey: ["auth-options"], queryFn: api.authOptions });

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await signIn(email, password);
    } catch (cause) {
      setError(failure(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6">
          <p className="mb-5 flex items-center gap-2">
            <BrandMark size="size-7" />
            <span className="label-tiny">Staff sign-in</span>
          </p>
          <h1 className="text-display font-semibold tracking-tight text-ink">
            Future Minds CMS
          </h1>
          <p className="mt-0.5 text-md text-ink-muted">
            Sign in to edit your site&rsquo;s content.
          </p>
        </header>

        {forgotten ? (
          <ForgotPassword onBack={() => setForgotten(false)} />
        ) : (
          <form onSubmit={onSubmit} noValidate className="panel grid gap-4 rounded-panel p-6">
            {error && <Notice tone="danger">{error}</Notice>}

            {/* A registration desk made on Members signs in with a username
                (D-127), which `type="email"` would refuse; and a phone left
                to itself capitalises or corrects either. */}
            <FieldShell label="Email or username" htmlFor={emailId} required>
              <TextInput
                id={emailId}
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </FieldShell>

            <FieldShell label="Password" htmlFor={passwordId} required>
              <TextInput
                id={passwordId}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </FieldShell>

            <Button
              type="submit"
              variant="primary"
              pending={pending}
              pendingLabel="Signing in…"
            >
              Sign in
            </Button>
            {options.data?.password_reset && (
              <Button type="button" variant="ghost" onClick={() => setForgotten(true)}>
                Forgot your password?
              </Button>
            )}
          </form>
        )}

        <p className="mt-4 text-sm text-ink-faint">
          Accounts are created by an administrator. Ask them if you do not have one yet.
        </p>
      </div>
    </main>
  );
}
